import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, withCtx as _withCtx, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = ["data-disbursement-id"]
const _hoisted_2 = { class: "action-icons" }
const _hoisted_3 = ["colspan"]
const _hoisted_4 = { class: "postdisb-wrap" }

import { ref, computed, onMounted, watch, nextTick } from "vue";
import FormField from "@/components/v2/certifications/inputs/FormField.vue";
import * as jsonpatch from "fast-json-patch";
import {  
  CreatePostDisbursementChangeRequest,
  DisbursementVM,
  DisbursementPatchRequest,
  ToggleEvent,
  DisbursementDetailsVM,
  DisbursementAddedEvent,
  DisbursementStatusIds,
} from "@/types/disbursements";
import { CertificationVM } from "@/types/certifications";
import { useDisbursementForm } from "@/composables/disbursements/useDisbursementForm";
import { $api } from "@/services/api1";
import { ExclamationCircleIcon } from '@heroicons/vue/24/outline';
import { FormOption } from "@/types/form";
import functions from "@/use/functions.js";
import { useForm } from "vee-validate";
import { Patch, PatchVM } from "@/types/json-patch";
import { formatInputDisplayValue, getFormChanges } from "@/use/formUtils";
import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";
import { Permissions, PermissionLevels } from "@/models/permissions";
import { useDisbursementsStore } from '@/store/store/disbursementsStore';
import PostDisbursementChangeForm from "@/components/v2/disbursements/PostDisbursementChangeForm.vue";
import * as DisbHelpers from '@/composables/disbursements/disbursementHelpers';


export default /*@__PURE__*/_defineComponent({
  __name: 'DisbursementsTableRow',
  props: {
    index: {},
    disbursement: {},
    certification: {},
    disbursements: {}
  },
  emits: ["toggleEdit", "disbursementUpdated", "disbursementAdded", "hasDirtyChanged", "postDisbursementChangeReady"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const postDisbursementFormRef = ref<InstanceType<typeof PostDisbursementChangeForm> | null>(null);
const pendingPostDisbursementChange = ref<CreatePostDisbursementChangeRequest | null>(null);
const hasPostDisbursementData = ref(false);
const editingPostDisbChangeId = ref<number | undefined>(undefined);
const store = useDisbursementsStore();

const props = __props;

const emit = __emit;

const disbursementVM = ref<DisbursementVM>(props.disbursement);
const isEditing = ref<boolean>(false);
const mode = ref<"add" | "edit" | "view">("view");
const formData = ref({} as any);
const formError = ref<boolean>(false);
const isSubmitting = ref<boolean>(false);
const initialValues = ref({} as DisbursementDetailsVM);
const isDirty = ref(false);
const initialized = ref(false);
const formKey = ref(0);
const showPostDisbursementForm = ref<boolean>(false);

const isDisbursed = computed(() => props.disbursement.details.isDisbursed);
const allowPostDisbursementChange = computed(() => props.disbursement.details.allowPostDisbursementChange);

const toggleIcon = computed(() =>
  isEditing.value ? "angle-up" : "angle-down"
);

const showToggleArrow = computed(
  () =>
    mode.value !== "add" &&
    (props.disbursement.details.disbursementStatusTypeId !==
      DisbursementStatusIds.POST_DISBURSEMENT_CANCELLATION ||
      showPostDisbursementForm.value)
);

const certificationVM = computed<CertificationVM>(() => {
  return props.certification;
});

const disbursementForms = computed<DisbursementVM[]>(() => props.disbursements);
const disbursementIndex = computed<number>(() => props.index);

const {
  formFields,
  formSchema,
  getDisbursementFormChanges,  
} = useDisbursementForm({
  certification: certificationVM,
  disbursement: disbursementVM,
  disbursementIndex: disbursementIndex,
  formData: formData,
  mode: mode,
  disbursementForms,
});

const {  
  errors,
  handleSubmit,  
  validate,
  setFieldError,
  setErrors,
  setValues,  
} = useForm({
  validationSchema: formSchema.value,
});

const onSubmit = handleSubmit(async (values) => {
  const { valid } = await validate();

  if (valid) {
    formData.value.disbursementAmount = values.disbursementAmount;
    formData.value.disbursementDate = values.disbursementDate;
    await confirmChanges();    
  } else {
    functions.openModal({
      title: "Error",
      description: "Missing required data.",
    });
  }
});

function resetChanges() {  
  mode.value = "view";
  isEditing.value = false;

  store.clearFormAmount(props.disbursement.details.id);
  
  nextTick(() => {
    const newData = props.disbursement?.form && !props.disbursement.details.isNew
      ? props.disbursement.form
      : props.disbursement.details;

    formData.value = JSON.parse(JSON.stringify(newData));
    initialValues.value = JSON.parse(JSON.stringify(newData));
        
    setValues({
      disbursementAmount: newData.disbursementAmount,
      disbursementDate: newData.disbursementDate,
      disbursementStatusTypeId: newData.disbursementStatusTypeId,
    });
    
    setErrors({});      
    formKey.value++;    
    pendingPostDisbursementChange.value = null;
    hasPostDisbursementData.value = false;
    showPostDisbursementForm.value = false;
    
    if (postDisbursementFormRef.value?.resetForm) {
      postDisbursementFormRef.value.resetForm();
    }
    
    isDirty.value = false;
    emit("hasDirtyChanged", { index: props.index, dirty: false });    
  });
}

async function checkIfDirty() {
  if (!initialized.value) return;
  
  let nowDirty = false;

  if (isDisbursed.value) {    
    if (hasPostDisbursementData.value && postDisbursementFormRef.value) {
      const isEditMode = postDisbursementFormRef.value.isInEditMode?.();
      
      if (isEditMode) {        
        const patches = await getPostDisbursementPatches();
        nowDirty = patches !== null && patches.patchDocument.length > 0;
      } else {        
        nowDirty = true;
      }
    }
  } else {    
    const diff = jsonpatch
      .compare(initialValues.value, formData.value)
      .filter((p) => p.path !== "/isEditing");
    
    nowDirty = diff.length > 0 || mode.value === "add" || hasPostDisbursementData.value;
  }
    
  if (nowDirty !== isDirty.value) {
    isDirty.value = nowDirty;
    emit("hasDirtyChanged", { index: props.index, dirty: nowDirty });
  }
}

function handleRowAction() {  
  if (!props.certification.isEditable || isSubmitting.value) return;

  if (!isEditing.value) {    
    isEditing.value = true;
    if (isDisbursed.value !== true) {
      mode.value = mode.value === 'add' ? 'add' : 'edit';
    }
    emit('toggleEdit', { index: props.index, flag: true });
  } else {    
    isEditing.value = false;
    if (mode.value !== 'add' && isDisbursed.value !== true) mode.value = 'view';
    emit('toggleEdit', { index: props.index, flag: false });
  }
}

function handleCreatePostDisbursement() {
  showPostDisbursementForm.value = true;
}

function handlePostDisbursementSaved() {
  showPostDisbursementForm.value = false;
  functions.openModal({
    title: "Success",
    description: "Post Disbursement Change has been created successfully.",
  });
}

async function handlePostDisbursementFormChanged(hasData: boolean) {
  hasPostDisbursementData.value = hasData;
  await checkIfDirty();
}

function handlePostDisbursementCancel() {
  const originalAmount = props.disbursement.details.disbursementAmount || 0;
  store.updateFormAmount(props.disbursement.details.id, originalAmount);  
  showPostDisbursementForm.value = false;
  hasPostDisbursementData.value = false;
  checkIfDirty();
}

async function saveIfDirty() {
  const diff = jsonpatch
    .compare(initialValues.value, formData.value)
    .filter((p) => p.path !== "/isEditing");
  if (mode.value === "add" || diff.length > 0) {
    await onSubmit(formData.value);
  }
}

function handleFieldUpdate(fieldName: string, fieldValue: any) {
  formData.value[fieldName] = fieldValue;

  if (fieldName === 'disbursementAmount') {    
    store.updateFormAmount(props.disbursement.details.id, fieldValue);
  }

  if (initialized.value && isEditing.value) {
    store.markDirty(props.disbursement.details.id);
  }
  checkIfDirty();
}

async function saveChanges(
  patchDoc: Patch[],
  changes: PatchVM[],
  modalHtml: string
) {
  isSubmitting.value = true;
  formError.value = false;

  if (mode.value === "add") {
    await addDisbursement(changes);
  } else {
    await updateDisbursement(patchDoc, changes, modalHtml);
  }
}

async function addDisbursement(changes: PatchVM[]) {
  const disbursementDatePatch = changes?.find(
    (c: PatchVM) => c.property === "disbursementDate"
  );

  if (disbursementDatePatch) {
    formData.value.disbursementDate = disbursementDatePatch?.value;
  }

  const requestVM = {
    disbursement: formData.value,
    certifiedAmount: props.certification.initialFormValues.certifiedAmount,
    certificationSequenceNumber:
      props.certification.initialFormValues.sequenceNumber,
    certificationId: props.certification.initialFormValues.id,
    formOptions: props.certification.disbursementFormOptions,
    appId: props.certification.appId,
    loanId: props.certification.loanId,
    cluid: props.certification.initialFormValues.cluid,
    lenderId: props.certification.header.lenderId,
    productTypeId: props.certification.productTypeId,
    referenceId: props.certification.referenceId,
    borrowerFirstName: props.certification.borrowers.borrower.firstName,
    borrowerLastName: props.certification.borrowers.borrower.lastName,
    disbursementDateValidated: true,
  };

  const response = await $api.disbursements.addDisbursement(requestVM);

  if (response.success) {
    formData.value = response.results;
    response.results.details.isEditing = false;
    response.results.form.isEditing = false;

    mode.value = "view";
    isEditing.value = false;
    isSubmitting.value = false;

    formData.value = response.results.details;
    disbursementVM.value = response.results;
    initialValues.value = JSON.parse(JSON.stringify(response.results.details));
    
    isDirty.value = false;
    emit("hasDirtyChanged", { index: props.index, dirty: false });

    emit("disbursementAdded", {
      disbursement: response.results,
      disbursementIndex: props.index,
    });

    if (response.errors.length > 0) {
      functions.openModal({
        title: "Alert",
        description: response.errors.join("."),
      });
    }
  } else {
    functions.openModal({
      title: "Error",
      description: "There was an issue adding the disbursement",
    });
  }

  isSubmitting.value = false;
}

async function updateDisbursement(
  patchDoc: Patch[],
  changes: PatchVM[],
  modalHtml: string
) {
  if (changes.length > 0) {
    const requestVM: DisbursementPatchRequest = {
      patchDocument: patchDoc,
      patchesVM: changes,
      certificationId: initialValues.value?.certificationId || 0,
      disbursementId: initialValues.value.id || 0,
      applicationId: props.certification.appId || 0,
      loanId: props.certification.loanId,
      certificationSequenceNumber: props.certification.form.sequenceNumber,
      disbursementSequenceNumber: initialValues.value?.sequenceNumber,
    };

    const response = await $api.disbursements.updateDisbursement(requestVM);

    if (response.success) {
      isSubmitting.value = false;
      isEditing.value = false;
      mode.value = "view";
      response.results.details.isEditing = false;
      response.results.form.isEditing = false;

      formData.value = response.results.details;
      disbursementVM.value = response.results;
      initialValues.value = JSON.parse(
        JSON.stringify(response.results.details)
      );
      
      isDirty.value = false;
      emit("hasDirtyChanged", { index: props.index, dirty: false });
      
      emit("disbursementUpdated", {
        disbursement: response.results,
        disbursementIndex: props.index,
      });
    } else {
      functions.openModal({
        title: "Error",
        html: modalHtml,
        description:
          "Please try again or reach out to SLC_Support for assistance.",
      });
    }
  } else {
    emit("toggleEdit", { index: props.index, flag: false });
    isEditing.value = false;
    mode.value = "view";
    isSubmitting.value = false;
  }

  isSubmitting.value = false;
}

function buildPatchVM(patch: Patch, hint?: string | null) {
  const fieldName = patch.path.split("/")[1];
  const field = formFields.value.find((field: any) => field.name === fieldName);

  const fieldLabel = field?.label;
  const fieldType = field?.type || "";
  const fieldOptions = field?.options as FormOption[] | undefined;
  const fieldDisplayValue =
    field?.name === "disbursementDate"
      ? formatInputDisplayValue(patch.value, fieldType, fieldOptions)
      : formatInputDisplayValue(
          formData.value[fieldName] ?? props.disbursement.details[fieldName],
          fieldType,
          fieldOptions
        );

  const patchVM = {
    ...patch,
    property: fieldName,
    label: fieldLabel,
    from: props.disbursement.details
      ? formatInputDisplayValue(
          initialValues.value[fieldName],
          fieldType,
          fieldOptions
        )
      : "",
    to: fieldDisplayValue,
    hint: hint,
    type: fieldType,
  };

  return patchVM;
}

function buildPostDisbPatchVM(patch: any): PatchVM {
  const fieldName = patch.path.substring(1); // Remove leading "/"
  
  const fieldNameMap: Record<string, string> = {
    'postDisbursementChangeReasonTypeId': 'Reason',
    'processingDate': 'Processing Date',
    'effectiveDate': 'Effective Date',
    'settleDate': 'Settle Date',
    'disbursementMethodTypeId': 'Method',
  };
  
  const displayName = fieldNameMap[fieldName] || fieldName;
  
  let fromValue = '';
  let toValue = '';
  
  if (patch.op === 'replace' || patch.op === 'add') {    
    const originalPostDisb = props.disbursement.details.latestPostDisbursementChange;
    const oldVal = originalPostDisb?.[fieldName];
    
    if (fieldName === 'postDisbursementChangeReasonTypeId') {
      const oldReason = props.certification.disbursementFormOptions?.postDisbursementChangeReasonTypes?.find(r => r.id === oldVal);
      const newReason = props.certification.disbursementFormOptions?.postDisbursementChangeReasonTypes?.find(r => r.id === patch.value);
      fromValue = oldReason?.label || String(oldVal || '');
      toValue = newReason?.label || String(patch.value);
    } else if (fieldName === 'disbursementMethodTypeId') {
      const oldMethod = props.certification.disbursementFormOptions?.disbursementMethodTypes?.find(m => m.id === oldVal);
      const newMethod = props.certification.disbursementFormOptions?.disbursementMethodTypes?.find(m => m.id === patch.value);
      fromValue = oldMethod?.label || String(oldVal || '');
      toValue = newMethod?.label || String(patch.value);
    } else if (fieldName.toLowerCase().includes('date')) {
      fromValue = oldVal ? DisbHelpers.formatDate(oldVal) : '';
      toValue = patch.value ? DisbHelpers.formatDate(patch.value) : '';
    } else {
      fromValue = String(oldVal || '');
      toValue = String(patch.value);
    }
  }
  
  return {
    ...patch,
    property: fieldName,
    label: displayName,
    from: fromValue,
    to: toValue,
    hint: null,
    type: null,
  };
}

const hasCertificationReceiveDate = computed<boolean>(() => {
  return (
    (
      props.certification.initialFormValues.certificationReceiveDate || ""
    ).trim().length > 0 &&
    props.certification.initialFormValues.certificationReceiveDate !==
      undefined &&
    props.certification.initialFormValues.certificationReceiveDate !== null
  );
});

async function confirmChanges() {
  if (props.disbursement && props.disbursement.details) {
    let patchDoc =
      mode.value === "add"
        ? getDisbursementFormChanges(
            initialValues.value,
            formData,
            formFields.value
          )
        : jsonpatch.compare(initialValues.value, formData.value);

    const ignoreFields = ["/isEditing"];

    const filteredPatchDoc = patchDoc.filter(
      (p: Patch) => !ignoreFields.includes(p.path)
    );
    let showModal = true;
    if (filteredPatchDoc && filteredPatchDoc.length > 0) {
      let patches: PatchVM[] = [];

      for (const patch of filteredPatchDoc) {
        if (patch.path === "/disbursementStatusTypeId" && patch.value === DisbursementStatusIds.PRE_DISBURSEMENT_CANCELLATION && initialValues.value.disbursementStatusTypeId !== DisbursementStatusIds.SCHEDULED_DISBURSEMENT) {
          showModal = false;
          setFieldError("disbursementStatusTypeId", "Pre-disbursement Cancellation is only possible if Disbursement is in Scheduled Disbursement state.");
        }
        else if (patch.path === "/disbursementStatusTypeId" && patch.value === DisbursementStatusIds.PRE_DISBURSEMENT_CANCELLATION && initialValues.value.disbursementStatusTypeId === DisbursementStatusIds.SCHEDULED_DISBURSEMENT) {
          filteredPatchDoc.push({
            op: "replace",
            path: "/disbursementAmount",
            value: 0,
          });
          formData.value.disbursementAmount = 0;
        } else if (patch.path === `/disbursementDate`) {
          const certificationReceiveDate = hasCertificationReceiveDate.value
            ? `${props.certification.form.certificationReceiveDate}T12:00:00.000Z`
            : null;

          const providedDate = `${formData.value.disbursementDate}T12:00:00.000Z`;

          const response = await $api.disbursements.validateDisbursementDate({
            productTypeId: props.certification.productTypeId,
            certificationDate: certificationReceiveDate,
            disbursementMethodTypeId:
              formData.value.disbursementMethodTypeId ||
              props.disbursement.details.disbursementMethodTypeId,
            providedDate: providedDate,
          });

          const hint = !response.isValid
            ? "The disbursement date has been set to the next eligible date of disbursement."
            : null;

          const validatedDisbursementDate = !response.isValid
            ? response.validatedDisbursementDateInput
            : response.disbursementDateInput;

          if (patch.op.toLowerCase() !== "remove") {
            (patch as Patch).value = validatedDisbursementDate;
          }

          patches.push(buildPatchVM(patch, hint));
        } else {
          patches.push(buildPatchVM(patch));
        }
      }

      if (showModal === true) 
      {
        let modalHtml = getFormChanges(patches, mode.value);

        functions.openModal({
          title: "Confirm",
          description: "Please review your changes before submitting...",
          html: modalHtml,
          buttons: [
            {
              title: "Cancel",
              onClick: () => {
                functions.closeModal();
                functions.openModal({
                  title: "Cancel",
                  description:
                    "This will exit the Edit mode without saving any changes. Disbursed Amount change is not saved. ",
                });
              },
            },
            {
              title: "Save Changes",
              onClick: async () => {
                functions.setModalLoading();
                await saveChanges(filteredPatchDoc, patches, modalHtml);
              },
            },
          ],
        });
      }
    } else {
      emit("toggleEdit", { index: props.index, flag: false });
      mode.value = "view";
      isEditing.value = false;
    }
  }
}

function setForm() {
  formData.value.isEditing = true;

  formData.value.disbursementStatusTypeId =
    props.disbursement.form?.disbursementStatusTypeId;

  formData.value.disbursementMethodTypeName =
    props.disbursement.form?.disbursementMethodTypeName;
}

function openPostDisbursementFormForEdit(
  data: CreatePostDisbursementChangeRequest,
  postDisbChangeId: number
) {  
  editingPostDisbChangeId.value = postDisbChangeId;  
  
  nextTick(() => {
    if (postDisbursementFormRef.value?.setFormData) {      
      postDisbursementFormRef.value.setFormData(data, postDisbChangeId);      
      nextTick(() => {
        hasPostDisbursementData.value = true;
      });
    }
  });
}

function expandAndOpenPostDisbursementForm(
  data: CreatePostDisbursementChangeRequest,
  postDisbChangeAmountLedgerId: number
) {
  
  showPostDisbursementForm.value = true;
    
  isEditing.value = true;  
  hasPostDisbursementData.value = true;  
  nextTick(() => {
    setTimeout(() => {
      if (postDisbursementFormRef.value?.setFormData) {        
        postDisbursementFormRef.value.setFormData(data, postDisbChangeAmountLedgerId);
      }
    }, 100);
  });  
}

async function getPostDisbursementPatches() {
  if (!hasPostDisbursementData.value || !postDisbursementFormRef.value) {    
    return null;
  }
  const isEditMode = postDisbursementFormRef.value.isInEditMode?.();
  const editingPostDisbChangeId = postDisbursementFormRef.value.getEditingPostDisbChangeId?.();  
  if (!isEditMode || !editingPostDisbChangeId) {    
    return null;
  }
  
  const initialPostDisbValues = postDisbursementFormRef.value.getInitialValues?.();
  const currentPostDisbValues = postDisbursementFormRef.value.getFormData?.();
  
  const diff = jsonpatch.compare(initialPostDisbValues, currentPostDisbValues);
  const filteredDiff = diff.filter(patch => {
    const fieldName = patch.path.substring(1);    
    return fieldName !== 'disbursementAmount' && 
           fieldName !== 'disbursementAmountDisplay' &&
           fieldName !== 'disbursementId';
  });
  
  if (filteredDiff.length === 0) {    
    return null;
  }
  
  const patchesVM: PatchVM[] = filteredDiff.map(patch => buildPostDisbPatchVM(patch));
  
  return {
    postDisbursementChangeAmountLedgerId: editingPostDisbChangeId,
    disbursementId: props.disbursement.details.id,
    certificationId: props.certification.id,
    patchDocument: filteredDiff,
    patchesVM: patchesVM
  };
}

__expose({ 
  saveIfDirty,
  resetChanges,
  getDisbursementId: () => props.disbursement.details.id,
  getSequenceNumber: () => props.disbursement.details.sequenceNumber,
  getFormData: () => formData.value,
  getPatches: async () => {
    if (isDisbursed.value) {
      return null;
    }

    const diff = jsonpatch
      .compare(initialValues.value, formData.value)
      .filter((p) => p.path !== "/isEditing");
    
    if (diff.length === 0) return null;
    
    const patchesVM: PatchVM[] = diff.map(patch => buildPatchVM(patch));

    return { 
      patches: diff, 
      patchesVM: patchesVM
    };
  },
  getPostDisbursementPatches,
  validateForm: async () => {
    let isValid = true;
    if (!isDisbursed.value) {      
      const { valid } = await validate();      
      if (!valid) {        
        isValid = false;
      }
    }
        
    if (hasPostDisbursementData.value && postDisbursementFormRef.value) {      
      const result = await postDisbursementFormRef.value.validateAndGetData();
      if (!result.valid) {        
        isValid = false;
      }
    }
    return isValid;
  },
  getPendingPostDisbursementChange: async () => {    
    if (!hasPostDisbursementData.value || !postDisbursementFormRef.value) {      
      return null;
    }
    const result = await postDisbursementFormRef.value.validateAndGetData();    
    if (!result.valid) {      
      return null;
    }
    
    const isEditMode = postDisbursementFormRef.value.isInEditMode();
    
    if (isEditMode) {            
      const patches = await getPostDisbursementPatches();
      return patches ? { patches } : null;
    } else {      
      const formData = postDisbursementFormRef.value.getFormData();
      return { data: formData };
    }
  },  
  openPostDisbursementFormForEdit,
  expandAndOpenPostDisbursementForm
});

onMounted(() => {
  disbursementVM.value = props.disbursement;

  if (props.disbursement?.form && !props.disbursement.details.isNew) {
    initialValues.value = JSON.parse(JSON.stringify(props.disbursement.form));
    formData.value = JSON.parse(JSON.stringify(props.disbursement.form));
    mode.value = "view";
    isEditing.value = false;
  } else {
    initialValues.value = JSON.parse(JSON.stringify(props.disbursement.details));
    formData.value = JSON.parse(JSON.stringify(props.disbursement.details));
    setForm();
    mode.value = "add";
    isEditing.value = true;
  }
  
  setTimeout(() => {
    initialized.value = true;
    if (mode.value === "add") {
      isDirty.value = true;
      emit("hasDirtyChanged", { index: props.index, dirty: true });
    }
  }, 0);
});

watch(
  () => props.certification.initialFormValues.certifiedAmount,
  () => {
    if (
      errors.value &&
      Object.keys(errors.value).includes("disbursementAmount")
    ) {
      validate();
    }
  }
);

watch(
  () => props.disbursement.details,
  (newDetails) => {
    if (!isEditing.value && mode.value !== "add") {
      initialValues.value = JSON.parse(JSON.stringify(newDetails));
      Object.keys(formData.value).forEach(key => {
        delete formData.value[key];
      });
      Object.assign(formData.value, JSON.parse(JSON.stringify(newDetails)));
    }
  },
  { deep: true }
);

watch(() => hasPostDisbursementData.value, (hasData) => {
  if (hasData && postDisbursementFormRef.value) {
    const isEditMode = postDisbursementFormRef.value.isInEditMode?.();
    
    // Only mark dirty on initial create, not on edit mode opening
    if (!isEditMode) {
      isDirty.value = true;
      emit('hasDirtyChanged', { index: props.index, dirty: true });
    }
  }
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("tr", {
      "data-disbursement-id": props.disbursement.details.id
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(formFields), (field) => {
        return (_openBlock(), _createElementBlock("td", {
          key: `${field.name}-${formKey.value}`
        }, [
          (_openBlock(), _createBlock(FormField, {
            key: `field-${field.name}-${formKey.value}`,
            isSubmitting: isSubmitting.value,
            isEditing: isEditing.value,
            field: field,
            mode: mode.value,
            modelValue: formData.value[field.name],
            "onUpdate:modelValue": ($event: any) => (handleFieldUpdate(field.name, $event))
          }, null, 8, ["isSubmitting", "isEditing", "field", "mode", "modelValue", "onUpdate:modelValue"]))
        ]))
      }), 128)),
      _createElementVNode("td", { onClick: handleRowAction }, [
        _createVNode(PermissionsWrapper, {
          disabled: !__props.certification.isEditable,
          showDisabledIfNotVisible: true,
          permissionIds: [_unref(Permissions).CertAndDisburse],
          permissionLevel: _unref(PermissionLevels).Update
        }, {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("div", _hoisted_2, [
              _createVNode(_component_font_awesome_icon, {
                class: "toggle",
                icon: ['fas', toggleIcon.value]
              }, null, 8, ["icon"]),
              _createVNode(_unref(ExclamationCircleIcon), {
                class: _normalizeClass(["icon-circle-exclamation", {
          'icon-circle-exclamation--visible':
          _unref(store).dirtyIds.has(props.disbursement.details.id)
          }])
              }, null, 8, ["class"])
            ], 512), [
              [_vShow, showToggleArrow.value]
            ])
          ]),
          _: 1
        }, 8, ["disabled", "permissionIds", "permissionLevel"])
      ])
    ], 8, _hoisted_1),
    _withDirectives(_createElementVNode("tr", null, [
      _createElementVNode("td", {
        colspan: _unref(formFields).length + 1
      }, [
        _createElementVNode("div", _hoisted_4, [
          (!showPostDisbursementForm.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "create-post-disb-button",
                onClick: handleCreatePostDisbursement
              }, " Create Post Disbursement Change "))
            : (_openBlock(), _createBlock(PostDisbursementChangeForm, {
                key: 1,
                ref_key: "postDisbursementFormRef",
                ref: postDisbursementFormRef,
                "is-visible": showPostDisbursementForm.value,
                disbursement: props.disbursement,
                certification: props.certification,
                onCancel: handlePostDisbursementCancel,
                onSaved: handlePostDisbursementSaved,
                onFormChanged: handlePostDisbursementFormChanged
              }, null, 8, ["is-visible", "disbursement", "certification"]))
        ])
      ], 8, _hoisted_3)
    ], 512), [
      [_vShow, (allowPostDisbursementChange.value || showPostDisbursementForm.value) && isEditing.value]
    ])
  ], 64))
}
}

})